import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loader } from "@/components/common";
import RepurposePanel from "@/components/RepurposePanel";
import Scorer from "@/components/Scorer";
import VideoCreator from "@/components/VideoCreator";
import { Sparkles, Copy, Image as ImageIcon, Film, Check, Wand2, Clapperboard } from "lucide-react";

const VIDEO_MAKERS = [
  ["CapCut", "https://www.capcut.com/editor"],
  ["Canva Video", "https://www.canva.com/video-editor/"],
  ["Clipchamp", "https://app.clipchamp.com/"],
];

function Section({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-surface border border-white/10 rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5" />} {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  return (
    <button data-testid={`copy-${id}`} onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }}
      className="text-white/40 hover:text-white transition-colors">
      {done ? <Check className="w-3.5 h-3.5 text-signal-green" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function Studio() {
  const loc = useLocation();
  const [topic, setTopic] = useState("");
  const [niche, setNiche] = useState("");
  const [angle, setAngle] = useState("");
  const [trendId, setTrendId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [pkg, setPkg] = useState(null);

  useEffect(() => {
    if (loc.state?.topic) {
      setTopic(loc.state.topic);
      setNiche(loc.state.niche || "");
      setAngle(loc.state.angle || "");
      setTrendId(loc.state.trend_id || null);
    }
  }, [loc.state]);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic or video idea"); return; }
    setLoading(true); setPkg(null);
    try {
      const r = await api.post("/content/generate", { topic, niche, angle, trend_id: trendId });
      setPkg(r.data);
      toast.success("Video package generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const genThumb = async () => {
    if (!pkg) return;
    setThumbLoading(true);
    try {
      const r = await api.post("/content/thumbnail", { package_id: pkg.id });
      setPkg((p) => ({ ...p, thumbnail_base64: r.data.thumbnail_base64 }));
      toast.success("Thumbnail created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Thumbnail failed");
    } finally { setThumbLoading(false); }
  };

  const markReady = async () => {
    await api.patch(`/content/${pkg.id}`, { status: "ready" });
    setPkg((p) => ({ ...p, status: "ready" }));
    toast.success("Marked ready to upload");
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <PageHeader eyebrow="AI Production Line" title="Content Studio" testid="studio-header"
        subtitle="Turn a trend into a complete, ready-to-film video package: titles, script, description, tags & thumbnail." />

      <div className="bg-surface border border-white/10 rounded-md p-5 mb-8 fade-up space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Video Idea / Topic</label>
          <input data-testid="topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. 5 AI tools that will replace your job in 2026"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 focus:border-brand focus:ring-1 focus:ring-brand/50 outline-none text-sm transition-all" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Niche</label>
            <input data-testid="studio-niche-input" value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="tech / finance / fitness"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 focus:border-brand outline-none text-sm transition-all" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Angle (optional)</label>
            <input data-testid="studio-angle-input" value={angle} onChange={(e) => setAngle(e.target.value)} placeholder="contrarian / beginner / listicle"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 focus:border-brand outline-none text-sm transition-all" />
          </div>
        </div>
        <button data-testid="generate-btn" onClick={generate} disabled={loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-medium rounded-md px-6 py-3 text-sm transition-colors flex items-center justify-center gap-2">
          <Wand2 className="w-4 h-4" /> {loading ? "Generating package..." : "Generate Video Package"}
        </button>
      </div>

      {loading && <Loader label="Writing your viral package" />}

      {pkg && (
        <div className="space-y-4 fade-up">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Section title="Title Options" icon={Sparkles}>
                <div className="space-y-2">
                  {(pkg.titles || []).map((t, i) => (
                    <div key={t} className="bg-[#0A0A0A] border border-white/5 rounded px-3 py-2" data-testid={`title-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm">{t}</span>
                        <CopyBtn text={t} id={`title-${i}`} />
                      </div>
                      <div className="mt-2"><Scorer text={t} kind="title" niche={pkg.niche} testid={`score-title-${i}`} /></div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="The Hook (first 10s)">
                <p className="text-sm text-white/80 italic leading-relaxed">"{pkg.hook}"</p>
                <div className="mt-3"><Scorer text={pkg.hook} kind="hook" niche={pkg.niche} testid="score-hook" /></div>
              </Section>

              <Section title="Full Script" icon={Film} action={<CopyBtn text={pkg.script} id="script" />}>
                <pre className="text-sm text-white/70 whitespace-pre-wrap font-sans leading-relaxed max-h-[420px] overflow-y-auto" data-testid="script-content">{pkg.script}</pre>
              </Section>

              <Section title="Description" action={<CopyBtn text={pkg.description} id="desc" />}>
                <p className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">{pkg.description}</p>
              </Section>
            </div>

            <div className="space-y-4">
              <Section title="Thumbnail" icon={ImageIcon} action={
                <button data-testid="gen-thumbnail-btn" onClick={genThumb} disabled={thumbLoading}
                  className="text-xs font-mono text-brand hover:text-brand-hover disabled:opacity-50 transition-colors">
                  {thumbLoading ? "rendering..." : pkg.thumbnail_base64 ? "regenerate" : "generate"}
                </button>
              }>
                {pkg.thumbnail_base64 ? (
                  <img data-testid="thumbnail-img" src={`data:image/png;base64,${pkg.thumbnail_base64}`} alt="thumbnail" className="w-full rounded border border-white/10 aspect-video object-cover" />
                ) : (
                  <div className="aspect-video rounded border border-dashed border-white/10 flex items-center justify-center text-white/30 text-xs font-mono">
                    {thumbLoading ? "rendering (~30s)..." : "no thumbnail yet"}
                  </div>
                )}
                {(pkg.thumbnail_concepts || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {pkg.thumbnail_concepts.map((c) => <li key={c} className="text-xs text-white/40 leading-snug">· {c}</li>)}
                  </ul>
                )}
              </Section>

              <Section title="Tags">
                <div className="flex flex-wrap gap-1.5">
                  {(pkg.tags || []).map((t) => <span key={t} className="text-[10px] font-mono text-white/50 border border-white/10 rounded px-2 py-0.5">{t}</span>)}
                </div>
              </Section>

              <Section title="Metadata">
                <div className="space-y-2 text-xs font-mono">
                  <div className="flex justify-between"><span className="text-white/40">LENGTH</span><span>{pkg.estimated_length}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-white/40">KEYWORD</span><span className="text-right text-signal-yellow">{pkg.target_keyword}</span></div>
                </div>
              </Section>

              <Section title="Monetization">
                <ul className="space-y-2">
                  {(pkg.monetization_tips || []).map((m) => <li key={m} className="text-xs text-signal-green/90 leading-snug flex gap-2"><span>$</span>{m}</li>)}
                </ul>
              </Section>
            </div>
          </div>

          <VideoCreator key={pkg.id} pkg={pkg} />

          <RepurposePanel pkg={pkg} onUpdate={(platforms) => setPkg((p) => ({ ...p, repurpose: platforms }))} />

          <div className="bg-surface border border-white/10 rounded-md p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-2 flex items-center gap-2"><Clapperboard className="w-3.5 h-3.5" /> Assemble your video</div>
              <div className="flex flex-wrap gap-2">
                {VIDEO_MAKERS.map(([name, url]) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" data-testid={`video-maker-${name.toLowerCase().split(' ')[0]}`}
                    className="text-sm border border-white/10 hover:border-brand/40 hover:text-brand rounded-md px-4 py-2 transition-colors flex items-center gap-2">
                    <Film className="w-3.5 h-3.5" /> {name}
                  </a>
                ))}
              </div>
            </div>
            <button data-testid="mark-ready-btn" onClick={markReady} disabled={pkg.status === "ready" || pkg.status === "uploaded"}
              className="bg-signal-yellow/10 border border-signal-yellow/30 text-signal-yellow hover:bg-signal-yellow/20 disabled:opacity-50 rounded-md px-6 py-2.5 text-sm font-medium transition-colors whitespace-nowrap">
              {pkg.status === "ready" || pkg.status === "uploaded" ? "✓ In Library" : "Save to Library"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
